import { mapGetters } from 'vuex'
import { hasPrivilegedAdminRole, hasIntegrationModuleRole, hasCompanyModuleRole, hasSuperAdminRole, hasVerifyCardListAccess, canAccessVerifyManage, hasMemberModuleRole, hasMemberModuleAccess, hasGiftIntegrationFlowPageRole, hasInnerGiftBatchPageAccess, hasInnerIntegrationStatsReportAccess, hasSupplierManagementAccess, canAccessSupplierProductPages } from '@/utils/privilegedRoleAccess'
import { canAccessSupplierSupplyProducts } from '@/utils/supplierSupplyProduct'

/**
 * 侧栏与移动端顶部模块条共用的菜单过滤（角色 / scope 与 Sidebar 原逻辑一致）。
 */
export default {
  computed: {
    ...mapGetters([
      'routers',
      'roles',
      'integrationModuleScope'
    ]),
    routes() {
      const roles = this.roles || []
      const scope = this.integrationModuleScope || {}
      const showPrivileged = hasPrivilegedAdminRole(roles)
      const showIntegration = hasIntegrationModuleRole(roles)
      const showCompany = hasCompanyModuleRole(roles)
      const isSuperAdmin = hasSuperAdminRole(roles)
      const showMember = hasMemberModuleRole(roles)

      /**
       * 超级管理员不受 scope flag 限制；其余用 scope 精控。
       * 若 scope 为 null（如首次加载前），按角色粗判兜底。
       */
      const flag = (key, fallback) => {
        if (isSuperAdmin) return true
        if (scope && scope[key] !== undefined) return !!scope[key]
        return fallback
      }
      const showGiftVerifyCard = flag('giftVerifyCardAllowed', showIntegration)
      const showActivation = flag('integrationActivationAllowed', showIntegration)
      /** 兑换积分批次：② / ⑤ / ⑥ integrationStatsAllowed（统计管理员只读查批次） */
      const showInnerGiftBatch = () => {
        if (isSuperAdmin) return true
        const g = scope && scope.giftIntegrationAllowed
        const a = scope && scope.integrationActivationAllowed
        const s = scope && scope.integrationStatsAllowed
        if (g !== undefined || a !== undefined || s !== undefined) {
          return g === true || a === true || s === true
        }
        return hasInnerGiftBatchPageAccess(roles, scope)
      }
      const showVerifyManage = canAccessVerifyManage(roles, scope)
      const showVerifyCardList = hasVerifyCardListAccess(roles, scope)
      const showMemberRegister = flag('memberRegisterAllowed', showMember)
      const showMemberSection = hasMemberModuleAccess(roles, scope)
      const showInnerIntegrationStats = hasInnerIntegrationStatsReportAccess(roles, scope)
      const showSupplierManage = hasSupplierManagementAccess(roles, scope)
      const showSupplierProductPages = canAccessSupplierProductPages(roles, scope, this.routers)
      const showSupplierSupplyProducts = canAccessSupplierSupplyProducts(roles, scope)

      /** 兑换积分流水：须 giftIntegrationAllowed 且具备 ② 相关角色；避免仅「赠送卡券管理员」时误显 */
      const showExchangeIntegrationHistory = () => {
        if (isSuperAdmin) return true
        if (scope && scope.giftIntegrationAllowed !== undefined && !scope.giftIntegrationAllowed) {
          return false
        }
        return hasGiftIntegrationFlowPageRole(roles)
      }

      return (this.routers || []).map((route) => {
        if (route.name === 'pyScript' || route.name === 'behaviorAnalysis') {
          return Object.assign({}, route, { hidden: !showPrivileged })
        }
        if (route.name === 'integration') {
          const children = (route.children || []).map((child) => {
            const next = Object.assign({}, child)
            if (child.meta && child.meta.superAdminOnly) {
              next.hidden = !isSuperAdmin
              return next
            }
            if (child.name === 'exchangeIntegrationHistory') {
              next.hidden = !showExchangeIntegrationHistory()
            } else if (child.name === 'adminGiftVerifyCard') {
              next.hidden = !showGiftVerifyCard
            } else if (child.name === 'innerGiftActivation') {
              next.hidden = !showActivation
            } else if (child.name === 'innerIntegrationGiftBatch') {
              next.hidden = !showInnerGiftBatch()
            } else if (child.name === 'innerIntegrationStatsReport') {
              next.hidden = !showInnerIntegrationStats
            } else if (child.name === 'innerIntegrationStatsDetail') {
              /** 仅从汇总页等入口进入；不在积分管理侧栏展示 */
              next.hidden = true
            } else if (child.name === 'innerIntegrationMonthlyByCompany') {
              /** 产品要求：不在积分管理侧栏展示「按月公司经营报表」（路由保留，可直链 / 书签） */
              next.hidden = true
            } else if (child.name === 'innerIntegrationMonthlyPeriodReport' || child.name === 'innerIntegrationStatsCharts') {
              next.hidden = true
            }
            return next
          })
          return Object.assign({}, route, { hidden: !showIntegration, children })
        }
        if (route.name === 'member') {
          const children = (route.children || []).map((child) => {
            const next = Object.assign({}, child)
            if (child.meta && child.meta.superAdminOnly) {
              next.hidden = !isSuperAdmin
              return next
            }
            if (child.name === 'memberRegister') {
              next.hidden = !showMemberRegister
            } else if (child.name === 'memberList') {
              next.hidden = !showMemberSection
            }
            return next
          })
          /** 会员入口与列表：①/②/③ 任一能力；注册子项仅 ①。与路由守卫 hasMemberModuleAccess 一致 */
          return Object.assign({}, route, { hidden: !showMemberSection, children })
        }
        if (route.name === 'oms') {
          const children = (route.children || []).map((child) => {
            const next = Object.assign({}, child)
            if (child.name === 'shipmentRecord' || (child.meta && child.meta.superAdminOnly)) {
              next.hidden = !isSuperAdmin
            }
            return next
          })
          return Object.assign({}, route, { children })
        }
        if (route.name === 'company') {
          return Object.assign({}, route, { hidden: !showCompany })
        }
        if (route.name === 'ums') {
          const children = (route.children || []).map((child) => {
            const next = Object.assign({}, child)
            if (child.hidden === true || (child.meta && child.meta.forceHideInSidebar)) {
              next.hidden = true
              return next
            }
            if (child.meta && child.meta.superAdminOnly) {
              next.hidden = !isSuperAdmin
            }
            return next
          })
          return Object.assign({}, route, { children })
        }
        if (route.name === 'verify') {
          const children = (route.children || []).map((child) => {
            const next = Object.assign({}, child)
            if (child.name === 'verifyManage') {
              next.hidden = !showVerifyManage
            } else if (child.name === 'verifyCardList') {
              next.hidden = !showVerifyCardList
            }
            return next
          })
          const showVerifySection = children.some((c) => !c.hidden)
          return Object.assign({}, route, { hidden: !showVerifySection, children })
        }
        if (route.name === 'supplier') {
          const children = (route.children || []).map((child) => {
            const next = Object.assign({}, child)
            if (child.name === 'pmsSupplier') {
              next.hidden = !showSupplierManage
            } else if (child.name === 'supplierProductList') {
              next.hidden = !showSupplierSupplyProducts
            } else if (child.name === 'pmsRealProduct' || child.name === 'pmsRealProductBackfill') {
              next.hidden = !showSupplierProductPages
            }
            return next
          })
          const showSupplierSection = children.some((c) => !c.hidden)
          return Object.assign({}, route, { hidden: !showSupplierSection, children })
        }
        return route
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/views/layout/components/Sidebar/sidebarMenuRoutes.js